import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../../frontend/DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import { getParaplanningCatalogById } from "../services/paraplanningChargesService";
import { formatCurrency } from "../utils/formatCurrency";
import "../../../css/Praplanning/ParaplanningChargesViewOnly.css";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

const CATALOGS = [
  {
    id: "financial_planning",
    label: "Financial Planning",
  },
  {
    id: "mortgage_protection",
    label: "Mortgage & Protection",
  },
];

const sortByDisplayOrder = (items = []) => {
  return [...items].sort(
    (a, b) =>
      (a.displayOrder || 999) - (b.displayOrder || 999) ||
      (a.name || "").localeCompare(b.name || ""),
  );
};

const ParaplanningChargesViewOnly = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [activeCatalogId, setActiveCatalogId] = useState("financial_planning");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadCatalogs = async () => {
      try {
        setLoading(true);
        setError("");

        const results = await Promise.all(
          CATALOGS.map(async (catalog) => {
            const data = await getParaplanningCatalogById(catalog.id);

            return {
              ...catalog,
              ...data,
              id: catalog.id,
              label: data?.name || catalog.label,
              categories: sortByDisplayOrder(data?.categories || []),
            };
          }),
        );

        if (isMounted) {
          setCatalogs(results);
        }
      } catch (err) {
        console.error("Failed to load paraplanning charges:", err);

        if (isMounted) {
          setError("Could not load paraplanning charges.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCatalogs();

    return () => {
      isMounted = false;
    };
  }, []);

  const activeCatalog = useMemo(() => {
    return catalogs.find((catalog) => catalog.id === activeCatalogId);
  }, [catalogs, activeCatalogId]);

  const filteredCategories = useMemo(() => {
    if (!activeCatalog?.categories?.length) return [];

    const search = searchTerm.trim().toLowerCase();

    if (!search) return activeCatalog.categories;

    return activeCatalog.categories
      .map((category) => {
        const filteredItems = sortByDisplayOrder(category.items || []).filter(
          (item) => {
            const itemText = [
              item.name,
              item.description,
              item.amount,
              ...(item.modifiers || []).map((modifier) => modifier.label),
            ]
              .join(" ")
              .toLowerCase();

            return itemText.includes(search);
          },
        );

        const categoryMatches = [category.name, category.description]
          .join(" ")
          .toLowerCase()
          .includes(search);

        if (categoryMatches) {
          return {
            ...category,
            items: sortByDisplayOrder(category.items || []),
          };
        }

        return {
          ...category,
          items: filteredItems,
        };
      })
      .filter((category) => category.items?.length);
  }, [activeCatalog, searchTerm]);

  return (
    <div className="main-content">
      <Helmet>
        <title>Paraplanning Charges | Rosemount</title>
      </Helmet>

      <Sidebar />

      <main className="Main-Container ParaplanningChargesView">
        <Breadcrumb
          items={[
            {
              label: "Paraplanning",
              to: "/Department/Paraplanning",
            },
            { label: "Charges" },
          ]}
        />
        <section className="ParaplanningChargesView__Header">
          <div className="ParaplanningChargesView__Eyebrow">
            <div className="FP_H3 ParaplanningChargesView__Title">
              Charges View
            </div>
            <div className="FP_Help_Text">
              View all Financial Planning and Mortgage & Protection charges in
              one place.
            </div>
          </div>

          <input
            type="search"
            className="ParaplanningChargesView__Search"
            placeholder="Search charges..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </section>

        <section className="ParaplanningChargesView__Tabs">
          {CATALOGS.map((catalog) => {
            const isActive = activeCatalogId === catalog.id;

            return (
              <button
                key={catalog.id}
                type="button"
                className={
                  isActive
                    ? "ParaplanningChargesView__Tab ParaplanningChargesView__Tab--Active"
                    : "ParaplanningChargesView__Tab"
                }
                onClick={() => setActiveCatalogId(catalog.id)}
              >
                {catalog.label}
              </button>
            );
          })}
        </section>

        {loading && (
          <div className="ParaplanningChargesView__State">
            Loading charges...
          </div>
        )}

        {!loading && error && (
          <div className="ParaplanningChargesView__State ParaplanningChargesView__State--Error">
            {error}
          </div>
        )}

        {!loading && !error && !filteredCategories.length && (
          <div className="ParaplanningChargesView__State">
            No charges found.
          </div>
        )}

        {!loading && !error && filteredCategories.length > 0 && (
          <section className="ParaplanningChargesView__Content">
            {filteredCategories.map((category) => (
              <article
                key={category.id}
                className="ParaplanningChargesView__Category"
              >
                <div className="ParaplanningChargesView__CategoryHeader">
                  <div>
                    <h2>{category.name}</h2>
                    {category.description && <p>{category.description}</p>}
                  </div>

                  <span>{category.items?.length || 0} case types</span>
                </div>

                <div className="ParaplanningChargesView__TableWrap">
                  <table className="ParaplanningChargesView__Table">
                    <thead>
                      <tr>
                        <th className="FP_Body">Case Type</th>
                        <th className="FP_Body">Description</th>
                        <th className="FP_Body">Base Cost</th>
                        <th className="FP_Body">Additional Charges</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortByDisplayOrder(category.items || []).map((item) => (
                        <tr key={item.id}>
                          <td>
                            <strong>{item.name}</strong>
                          </td>

                          <td>{item.description || "-"}</td>

                          <td>
                            <strong>
                              {formatCurrency(Number(item.amount || 0))}
                            </strong>
                          </td>

                          <td>
                            {item.modifiers?.length ? (
                              <div className="ParaplanningChargesView__Modifiers">
                                {sortByDisplayOrder(item.modifiers).map(
                                  (modifier) => (
                                    <div
                                      key={modifier.id}
                                      className="ParaplanningChargesView__Modifier"
                                    >
                                      <span>{modifier.label}</span>
                                      <strong>
                                        {modifier.amountPrefix || "+"}
                                        {formatCurrency(
                                          Number(modifier.amount || 0),
                                        )}
                                      </strong>
                                    </div>
                                  ),
                                )}
                              </div>
                            ) : (
                              <span className="ParaplanningChargesView__Empty">
                                No additional charges
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
};

export default ParaplanningChargesViewOnly;
